import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { trackTabClick } from '../services/analyticsService';
import {
  FaUserCircle,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { getPortalEmoji } from '../utils/portalEmojis';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import "./Sidebar.css";

/**
 * Padlock pinned to the far right of a nav row. Grayscale + knocked back so it
 * reads as a quiet status marker rather than a second colored icon.
 */
const LockBadge = () => (
  <span
    className="ml-auto pl-2 text-[11px] leading-none grayscale opacity-50 flex-shrink-0"
    role="img"
    aria-label="Locked"
  >
    🔒
  </span>
);

const StartSidebar = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  const [hiddenItems, setHiddenItems] = useState([]);
  const navigationRef = useRef(null);
  const seeMoreRef = useRef(null);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile(userData);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [currentUser]);

  // Refresh profile data when location changes (user navigates)
  useEffect(() => {
    if (!currentUser) return;

    const refreshProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile(userData);
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    };

    // Refresh when user navigates (this will catch profile updates)
    refreshProfile();
  }, [currentUser, location.pathname]);

  // Emojis come from the canonical portal map so /start and /portal show the
  // same glyph for the same concept.
  // Everything but the dashboard is a locked preview of the real portal page, so
  // each of those rows carries the padlock.
  const menuItems = [
    { icon: getPortalEmoji('dashboard'), text: 'Dashboard', path: '/start', alwaysVisible: true },
    { icon: getPortalEmoji('roadmap'), text: 'Roadmap', path: '/start/roadmap', locked: true },
    { icon: getPortalEmoji('courses'), text: 'Courses', path: '/start/courses', locked: true },
    { icon: getPortalEmoji('community'), text: 'Community', path: '/start/community', locked: true },
    { icon: getPortalEmoji('sales'), text: 'Sales', path: '/start/sales', locked: true },
    { icon: getPortalEmoji('operations'), text: 'Operations', path: '/start/operations', locked: true },
    { icon: getPortalEmoji('technology'), text: 'Technology', path: '/start/technology', locked: true },
    { icon: getPortalEmoji('finance'), text: 'Finance', path: '/start/finance', locked: true },
    { icon: getPortalEmoji('marketing'), text: 'Marketing', path: '/start/marketing', locked: true },
    { icon: getPortalEmoji('hr'), text: 'HR', path: '/start/hr', locked: true }
  ];

  // Calculate which items fit and which need to be hidden
  useEffect(() => {
    const calculateVisibleItems = () => {
      if (!navigationRef.current) return;

      const containerHeight = window.innerHeight;
      const logoHeight = 80; // Approximate logo section height
      const userProfileHeight = 60; // Approximate user profile section height
      const seeMoreButtonHeight = 48; // Approximate "See More" button height
      const bottomSectionHeight = userProfileHeight + seeMoreButtonHeight;
      const availableHeight = containerHeight - logoHeight - bottomSectionHeight;
      
      const menuItemHeight = 56; // Approximate height per menu item (py-4 = 16px top + 16px bottom + 24px content)
      const maxVisibleItems = Math.floor(availableHeight / menuItemHeight);

      if (menuItems.length > maxVisibleItems) {
        const visible = menuItems.slice(0, maxVisibleItems - 1); // -1 to make room for "See More"
        const hidden = menuItems.slice(maxVisibleItems - 1);
        setVisibleItems(visible);
        setHiddenItems(hidden);
      } else {
        setVisibleItems(menuItems);
        setHiddenItems([]);
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateVisibleItems, 100);
    window.addEventListener('resize', calculateVisibleItems);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateVisibleItems);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (seeMoreRef.current && !seeMoreRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMoreMenu]);

  const isItemActive = (path) => {
    const current = location.pathname.replace(/\/$/, '') || '/start';
    if (path === '/start') return current === '/start';
    return current === path || current.startsWith(`${path}/`);
  };

  const displayName = loading
    ? 'Loading...'
    : userProfile?.firstName && userProfile?.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`
      : userProfile?.firstName
        ? userProfile.firstName
        : userProfile?.displayName || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Guest User';

  return (
    <div
      className={`h-screen bg-white dark:bg-gray-800 shadow-md flex flex-col justify-between fixed top-0 left-0 transition-all duration-300 ${
        isCollapsed ? 'w-20' : 'w-64'
      }`}
      style={{
        zIndex: 1000,
        height: '100dvh', // Dynamic viewport height for better mobile support
        maxHeight: '100dvh',
        overflow: 'visible'
      }}
    >
      {/* Collapse/Expand Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 shadow-lg flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 z-50"
        style={{ right: '-16px', transform: 'translateY(-50%)' }}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <FaChevronRight className="text-gray-600 dark:text-gray-300 text-sm" />
        ) : (
          <FaChevronLeft className="text-gray-600 dark:text-gray-300 text-sm" />
        )}
      </button>

      {/* Top Section */}
      <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden min-h-0">
        {/* Logo */}
        <div className="flex justify-center items-center py-6 px-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
          <Link to="/start" className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
            <img
              src="/bba-emblem.svg"
              alt="BBA Emblem"
              className={`${isCollapsed ? 'h-10' : 'h-12'} w-auto object-contain`}
            />
            {!isCollapsed && (
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">Builders</span>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">Breakthrough</span>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">Advisors</span>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <div
          ref={navigationRef}
          className="py-2 overflow-y-auto flex-1 min-h-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          {visibleItems.map((item, index) => {
            const active = isItemActive(item.path);
            return (
              <div
                key={index}
                className={`group flex items-center rounded-xl transition-colors duration-200 cursor-pointer ${
                  isCollapsed ? 'justify-center mx-2 my-1 py-3 px-2' : 'mx-3 my-0.5 py-2.5 px-3'
                } ${
                  active
                    ? 'bg-red-50 dark:bg-gray-700/70'
                    : 'hover:bg-red-50/60 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => { trackTabClick(item.text.toLowerCase(), item.path); navigate(item.path); }}
                title={isCollapsed ? item.text : ''}
              >
                <span
                  className={`flex items-center justify-center leading-none ${
                    isCollapsed ? 'text-2xl' : 'text-xl mr-3 w-7'
                  }`}
                  aria-hidden="true"
                >
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <>
                    <span className={`text-[17px] tracking-tight ${
                      active ? 'font-semibold' : 'font-medium'
                    } ${
                      active
                        ? 'text-red-600 dark:text-red-300'
                        : 'text-gray-700 dark:text-gray-200'
                    }`}>{item.text}</span>
                    {item.locked && <LockBadge />}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Section - Always visible */}
      <div
        className="relative flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700"
        ref={seeMoreRef}
        style={{
          minHeight: hiddenItems.length > 0 ? '108px' : '60px', // Account for "See More" button if present
          flexShrink: 0
        }}
      >
        {/* See More dropdown */}
        {hiddenItems.length > 0 && showMoreMenu && (
          <div className={`absolute bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] z-50 ${
            isCollapsed
              ? 'bottom-0 left-full ml-2 w-48'
              : 'bottom-full left-0 right-0 mb-1'
          }`}>
            {hiddenItems.map((item, index) => (
              <div
                key={index}
                className="flex items-center py-3 px-5 transition-colors duration-200 cursor-pointer hover:bg-red-50 dark:hover:bg-gray-700"
                onClick={() => {
                  trackTabClick(item.text.toLowerCase(), item.path);
                  navigate(item.path);
                  setShowMoreMenu(false);
                }}
              >
                <span className="mr-3 text-xl leading-none w-6 flex items-center justify-center" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="text-base font-medium tracking-tight text-gray-700 dark:text-gray-200">{item.text}</span>
                {item.locked && <LockBadge />}
              </div>
            ))}
          </div>
        )}

        {/* See More: click toggles open/closed only (no hover — avoids flicker when moving to the popup, especially collapsed) */}
        {hiddenItems.length > 0 && (
          <button
            type="button"
            className={`w-full border-t border-gray-100 dark:border-gray-700 text-left transition-colors duration-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
              isCollapsed ? 'py-3 px-2 flex justify-center items-center' : 'py-3 px-4 flex items-center justify-between'
            }`}
            onClick={() => setShowMoreMenu((open) => !open)}
            aria-expanded={showMoreMenu}
            aria-haspopup="true"
            title={isCollapsed ? `See More (${hiddenItems.length})` : undefined}
          >
            {!isCollapsed && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                See More ({hiddenItems.length})
              </span>
            )}
            <FaChevronDown className={`text-gray-500 dark:text-gray-400 transition-transform duration-200 shrink-0 ${showMoreMenu ? 'rotate-180' : ''} ${isCollapsed ? 'text-xl' : ''}`} />
          </button>
        )}

        {/* User Profile */}
        <div
          className={`border-t border-gray-100 dark:border-gray-700 transition-colors duration-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
            isCollapsed ? 'py-3 px-2 flex justify-center' : 'py-3 px-4 flex'
          }`}
          onClick={() => {
            setShowMoreMenu(false);
            navigate('/start/profile');
          }}
          title={isCollapsed ? displayName : ''}
        >
          <div className="h-9 w-9 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden flex items-center justify-center flex-shrink-0">
            {loading ? (
              <FaUserCircle className="h-full w-full text-gray-400 dark:text-gray-300 p-1" />
            ) : userProfile?.profileImage ? (
              <img
                src={userProfile.profileImage}
                alt="User"
                className="h-full w-full object-cover"
              />
            ) : (
              <FaUserCircle className="h-full w-full text-gray-400 dark:text-gray-300 p-1" />
            )}
          </div>
          {!isCollapsed && (
            <div className="ml-3 flex flex-col min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                {displayName}
              </div>
              {userProfile?.company && (
                <div
                  className="text-xs font-medium truncate mt-0.5"
                  style={{ color: userProfile?.companyColors?.primary || '#6b7280' }}
                >
                  {userProfile.company}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StartSidebar;
